// src/pages/Proyectos.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Col, Row, Button, Label, Spinner, Alert } from "reactstrap";
import ProyectoList from "../components/Proyecto/ProyectoList";
import NewProyectoModal from "../components/Proyecto/NewProyectoModal";
import axios from "axios";
import { API_URL_proyectos, API_URL_subproyectos, API_URL_sedes } from "../constants";
import Papa from "papaparse";
import Moment from "moment";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAuthHeaders } from "../utils/auth";
import { Page, PageHeader, Card, COLOR } from "../components/ui";

// ---- Helpers ----
const normalize = (v) => (v ?? "").toString().trim().toLowerCase();
const toSelectOptions = (arr) => arr.map((v) => ({ value: v, label: v }));

// Portal react-select menus to <body> so they can't be covered by cards
// that sit below them in the DOM.
const SELECT_MENU_PORTAL_PROPS = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
};

// Custom styles for react-select to make dropdowns bigger
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    fontSize: '1rem',
    borderColor: state.isFocused ? '#80bdff' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
    '&:hover': {
      borderColor: '#80bdff',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    minHeight: '36px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '1rem',
    color: '#6c757d',
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '1rem',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    fontSize: '1rem',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '1rem',
    padding: '8px 12px',
    backgroundColor: state.isSelected
      ? '#007bff'
      : state.isFocused
      ? '#e9ecef'
      : 'white',
    color: state.isSelected ? 'white' : '#212529',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#007bff',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '4px',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '4px',
  }),
};

// CSV Utilities
const CsvUtils = {
  downloadWithBOM: (data, filename, delimiter = ",") => {
    if (!data?.length) {
      toast.info("No hay datos para exportar.");
      return;
    }

    const csv = Papa.unparse(data, {
      delimiter,
      quotes: true,
      newline: "\r\n",
      header: true,
    });

    const csvWithBOM = "\uFEFF" + csv;
    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  validateFile: (file) => {
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ["text/csv", "application/vnd.ms-excel"];

    if (!file) {
      return { valid: false, error: "No se seleccionó archivo" };
    }

    if (file.size > MAX_SIZE) {
      return {
        valid: false,
        error: "El archivo es demasiado grande (máx 10MB)",
      };
    }

    if (!ALLOWED_TYPES.includes(file.type) && !file.name.endsWith(".csv")) {
      return { valid: false, error: "Solo se permiten archivos CSV" };
    }

    return { valid: true };
  },

  normalizeDates: (row) => {
    const out = { ...row };
    Object.keys(out).forEach((k) => {
      const v = out[k];
      if (v == null || v === "") return;

      const m = Moment(
        v,
        [
          Moment.ISO_8601,
          "YYYY-MM-DD",
          "M/D/YYYY",
          "DD/MM/YYYY",
          "MM/DD/YYYY",
          "YYYY/MM/DD",
        ],
        true
      );

      if (m.isValid()) {
        out[k] = m.format("YYYY-MM-DD");
      }
    });
    return out;
  },
};

const Proyectos = () => {
  const [proyectos, setProyectos] = useState([]);
  const [subproyectos, setSubproyectos] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  // Filters
  const [selectedModalidad, setSelectedModalidad] = useState(null);
  const [selectedLider, setSelectedLider] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedSede, setSelectedSede] = useState(null);

  const fetchProyectos = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const headers = getAuthHeaders();

    try {
      const [proyectosRes, subproyectosRes, sedesRes] = await Promise.allSettled([
        axios.get(API_URL_proyectos, { headers, timeout: 10000 }),
        axios.get(API_URL_subproyectos, { headers, timeout: 10000 }),
        axios.get(API_URL_sedes, { headers, timeout: 10000 }),
      ]);

      if (proyectosRes.status === "fulfilled") {
        setProyectos(proyectosRes.value.data || []);
      } else {
        console.error("Error fetching proyectos:", proyectosRes.reason);
        toast.error("No se pudieron cargar los proyectos.");
      }

      if (subproyectosRes.status === "fulfilled") {
        setSubproyectos(subproyectosRes.value.data || []);
      } else {
        console.error("Error fetching subproyectos:", subproyectosRes.reason);
      }

      if (sedesRes.status === "fulfilled") {
        setSedes(sedesRes.value.data || []);
      } else {
        console.error("Error fetching sedes:", sedesRes.reason);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("Error inesperado al cargar datos");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProyectos();
  }, [fetchProyectos]);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) {
      setFile(null);
      return;
    }

    const validation = CsvUtils.validateFile(selectedFile);

    if (!validation.valid) {
      toast.error(validation.error);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setFile(selectedFile);
  }, []);

  const updateProyectosFromCSV = useCallback(
    async (result) => {
      const rows = (result?.data || []).map((r) => CsvUtils.normalizeDates(r));

      if (!rows.length) {
        toast.warn("El CSV parece vacío.");
        return;
      }

      const headers = getAuthHeaders();
      let successCount = 0;
      let errorCount = 0;

      for (const [index, entry] of rows.entries()) {
        try {
          await axios.post(API_URL_proyectos, entry, { headers });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Error en fila ${index + 2}:`, err?.response?.data || err);
        }
      }

      if (successCount > 0) {
        toast.success(`✓ ${successCount} proyecto(s) cargado(s) exitosamente`);
        fetchProyectos();
      }

      if (errorCount > 0) {
        toast.error(`✗ ${errorCount} error(es) al cargar. Verifica la consola.`);
      }

      // Clear file input
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [fetchProyectos]
  );

  const handleFileUpload = useCallback(() => {
    if (!file) {
      toast.warn("Selecciona un archivo CSV primero.");
      return;
    }

    setIsUploading(true);

    const reader = new FileReader();

    reader.onload = (event) => {
      Papa.parse(event.target.result, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          updateProyectosFromCSV(result);
          setIsUploading(false);
        },
        error: (err) => {
          console.error("Papa parse error:", err);
          toast.error("Error al leer el CSV");
          setIsUploading(false);
        },
      });
    };

    reader.onerror = () => {
      console.error("FileReader error:", reader.error);
      toast.error("Error al leer el archivo");
      setIsUploading(false);
    };

    reader.onabort = () => {
      toast.error("Lectura de archivo cancelada");
      setIsUploading(false);
    };

    reader.readAsText(file);
  }, [file, updateProyectosFromCSV]);

  // Filter options derived from proyectos
  const modalidadOptions = useMemo(
    () => [...new Set((proyectos || []).map((p) => p.modalidad).filter(Boolean))].sort(),
    [proyectos]
  );

  const liderOptions = useMemo(
    () => [...new Set((proyectos || []).map((p) => p.lider).filter(Boolean))].sort(),
    [proyectos]
  );

  const statusOptions = useMemo(
    () => [...new Set((proyectos || []).map((p) => p.status).filter(Boolean))].sort(),
    [proyectos]
  );

  const cityOptions = useMemo(
    () => [...new Set((proyectos || []).map((p) => p.ciudad).filter(Boolean))].sort(),
    [proyectos]
  );

  const countryOptions = useMemo(
    () => [...new Set((proyectos || []).map((p) => p.pais).filter(Boolean))].sort(),
    [proyectos]
  );

  const sedeOptions = useMemo(
    () => sedes.map((s) => s.sede).filter(Boolean).sort(),
    [sedes]
  );

  // Apply filters to proyectos
  const filteredProyectos = useMemo(() => {
    const TERMINADO = "Terminado";

    return (proyectos || []).filter((p) => {
      // Default behavior: if no Status selected, HIDE "Terminado"
      if (!selectedStatus) {
        if (normalize(p.status) === normalize(TERMINADO)) return false;
      } else {
        if (normalize(p.status) !== normalize(selectedStatus)) return false;
      }

      if (selectedModalidad && normalize(p.modalidad) !== normalize(selectedModalidad))
        return false;
      if (selectedLider && normalize(p.lider) !== normalize(selectedLider)) return false;
      if (selectedCity && normalize(p.ciudad) !== normalize(selectedCity)) return false;
      if (selectedCountry && normalize(p.pais) !== normalize(selectedCountry))
        return false;
      if (selectedSede && normalize(p.sede_name) !== normalize(selectedSede)) return false;

      return true;
    });
  }, [
    proyectos,
    selectedModalidad,
    selectedLider,
    selectedStatus,
    selectedCity,
    selectedCountry,
    selectedSede,
  ]);

  // Download CSV
  const downloadProyectosCSV = useCallback(() => {
    if (!filteredProyectos || filteredProyectos.length === 0) {
      toast.info("No hay proyectos para exportar.");
      return;
    }

    const rows = filteredProyectos.map((p) => {
      const out = { ...p };

      // Remove pk
      delete out.pk;

      // 'Value Stream' por nombre (sede es el id numérico)
      if (out.sede !== undefined || out.sede_name !== undefined) {
        out["Value Stream"] = out.sede_name ?? out.sede;
        delete out.sede;
        delete out.sede_name;
      }

      // Convert dates to YYYY-MM-DD if valid
      Object.keys(out).forEach((k) => {
        const v = out[k];
        if (v == null || v === "") return;

        const m = Moment(
          v,
          [Moment.ISO_8601, "YYYY-MM-DD", "M/D/YYYY", "DD/MM/YYYY"],
          true
        );

        if (m.isValid()) {
          out[k] = m.format("YYYY-MM-DD");
        }
      });

      return out;
    });

    const dateStr = new Date().toISOString().slice(0, 10);
    CsvUtils.downloadWithBOM(rows, `Proyectos_${dateStr}.csv`);
    toast.success("CSV descargado exitosamente");
  }, [filteredProyectos]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSelectedModalidad(null);
    setSelectedLider(null);
    setSelectedStatus(null);
    setSelectedCity(null);
    setSelectedCountry(null);
    setSelectedSede(null);
  }, []);

  const activeFilterCount = [
    selectedModalidad, selectedLider, selectedStatus,
    selectedCity, selectedCountry, selectedSede,
  ].filter(Boolean).length;

  return (
    <Page>
      <PageHeader
        title="Proyectos"
        subtitle="Usa los filtros de Modalidad, Líder, Status, Ciudad, País y Value Stream para reducir la lista de proyectos."
        actions={<NewProyectoModal create={true} resetState={fetchProyectos} />}
      />

      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card
        title="Filtros"
        headerRight={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <small style={{ color: COLOR.muted }}>
              {activeFilterCount > 0 ? `${activeFilterCount} activo${activeFilterCount === 1 ? '' : 's'}` : 'Sin filtros'}
            </small>
            <Button color="link" size="sm" onClick={handleClearFilters} disabled={!activeFilterCount}>
              Limpiar
            </Button>
          </span>
        }
      >
        <Row>
          <Col md={4} className="mb-3">
            <Label htmlFor="modalidad-filter">Modalidad</Label>
            <Select
              inputId="modalidad-filter"
              options={toSelectOptions(modalidadOptions)}
              isClearable
              isSearchable
              placeholder="Todas"
              styles={customSelectStyles}
              {...SELECT_MENU_PORTAL_PROPS}
              value={
                selectedModalidad
                  ? { value: selectedModalidad, label: selectedModalidad }
                  : null
              }
              onChange={(o) => setSelectedModalidad(o?.value || null)}
              aria-label="Filtrar por modalidad"
            />
          </Col>
          <Col md={4} className="mb-3">
            <Label htmlFor="lider-filter">Líder</Label>
            <Select
              inputId="lider-filter"
              options={toSelectOptions(liderOptions)}
              isClearable
              isSearchable
              placeholder="Todos"
              styles={customSelectStyles}
              {...SELECT_MENU_PORTAL_PROPS}
              value={selectedLider ? { value: selectedLider, label: selectedLider } : null}
              onChange={(o) => setSelectedLider(o?.value || null)}
              aria-label="Filtrar por líder"
            />
          </Col>
          <Col md={4} className="mb-3">
            <Label htmlFor="status-filter">Status</Label>
            <Select
              inputId="status-filter"
              options={toSelectOptions(statusOptions)}
              isClearable
              isSearchable
              placeholder="Todos (excepto Terminado)"
              styles={customSelectStyles}
              {...SELECT_MENU_PORTAL_PROPS}
              value={
                selectedStatus ? { value: selectedStatus, label: selectedStatus } : null
              }
              onChange={(o) => setSelectedStatus(o?.value || null)}
              aria-label="Filtrar por status"
            />
          </Col>
          <Col md={4} className="mb-3">
            <Label htmlFor="city-filter">Ciudad</Label>
            <Select
              inputId="city-filter"
              options={toSelectOptions(cityOptions)}
              isClearable
              isSearchable
              placeholder="Todas"
              styles={customSelectStyles}
              {...SELECT_MENU_PORTAL_PROPS}
              value={selectedCity ? { value: selectedCity, label: selectedCity } : null}
              onChange={(o) => setSelectedCity(o?.value || null)}
              aria-label="Filtrar por ciudad"
            />
          </Col>
          <Col md={4} className="mb-3">
            <Label htmlFor="country-filter">País</Label>
            <Select
              inputId="country-filter"
              options={toSelectOptions(countryOptions)}
              isClearable
              isSearchable
              placeholder="Todos"
              styles={customSelectStyles}
              {...SELECT_MENU_PORTAL_PROPS}
              value={
                selectedCountry ? { value: selectedCountry, label: selectedCountry } : null
              }
              onChange={(o) => setSelectedCountry(o?.value || null)}
              aria-label="Filtrar por país"
            />
          </Col>
          <Col md={4} className="mb-3">
            <Label htmlFor="sede-filter">Value Stream</Label>
            <Select
              inputId="sede-filter"
              options={toSelectOptions(sedeOptions)}
              isClearable
              isSearchable
              placeholder="Todas"
              styles={customSelectStyles}
              {...SELECT_MENU_PORTAL_PROPS}
              value={selectedSede ? { value: selectedSede, label: selectedSede } : null}
              onChange={(o) => setSelectedSede(o?.value || null)}
              aria-label="Filtrar por Value Stream"
            />
          </Col>
        </Row>
      </Card>

      <Card
        title="Importar / Exportar"
        headerRight={
          file && (
            <small style={{ color: COLOR.muted }}>
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </small>
          )
        }
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            color="info"
            onClick={downloadProyectosCSV}
            disabled={isLoading || !filteredProyectos.length}
            aria-label="Descargar proyectos en formato CSV"
          >
            📥 Descargar CSV
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isUploading}
            aria-label="Seleccionar archivo CSV para importar"
          />

          <Button
            color="primary"
            onClick={handleFileUpload}
            disabled={!file || isUploading}
            aria-label="Subir archivo CSV seleccionado"
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Subiendo...
              </>
            ) : (
              "📤 Subir CSV"
            )}
          </Button>
        </div>
      </Card>

      <Card
        title={
          <>
            Proyectos
            <span style={{ color: COLOR.muted, fontWeight: 500, marginLeft: 8 }}>
              {filteredProyectos.length}
              {filteredProyectos.length !== proyectos.length && ` de ${proyectos.length}`}
            </span>
          </>
        }
        padded={false}
        scrollX
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <Spinner color="primary" />
            <p style={{ marginTop: 8, color: COLOR.muted }}>Cargando proyectos...</p>
          </div>
        ) : (
          <ProyectoList
            proyectos={filteredProyectos}
            resetState={fetchProyectos}
            itemsPerPage={10}
          />
        )}
      </Card>

      <ToastContainer position="bottom-right" autoClose={4000} />
    </Page>
  );
};

export default Proyectos;